import { generateTestId } from "@client-shared";
import { exportToCsv, type CsvColumn } from "@client-shared/utils";
import type { PurchaseOrder, PurchaseOrderLine } from "@shared/types";
import type { TFunction } from "i18next";

export const setOrderWaitingSummaryDialogTestId = (identifier: string): string => generateTestId("OrderWaitingSummaryDialog", identifier);

/**
 * Exact per-unit bag weight, in grams, derived from lineItemWeightInGrams / quantity —
 * more reliable than parsing free-text variant titles (bag size isn't always stated there,
 * or stated consistently). Rounded to the nearest gram to absorb rounding noise upstream and
 * keep line items for the same physical bag size grouped together.
 */
export const getUnitWeightGrams = (lineItem: Pick<PurchaseOrderLine, "lineItemWeightInGrams" | "quantity">): number | undefined => {
  const weightInGrams = Number(lineItem.lineItemWeightInGrams ?? 0);
  const quantity = Number.parseInt(String(lineItem.quantity), 10);
  if (!weightInGrams || !Number.isFinite(quantity) || quantity <= 0) return undefined;
  return Math.round(weightInGrams / quantity);
};

export interface OrderWaitingSummaryProduct {
  readonly key: string;
  readonly productTitle: string;
  readonly bagSizeGrams?: number;
  readonly grindType?: string;
  readonly totalUnits: number;
  readonly totalWeight: number;
}

/**
 * Groups WaitingToBeFulfilled line items by (productTitle, bagSizeGrams, grindType) —
 * one flattened row per combination, sorted by coffee name then bag size.
 */
export const getOrderWaitingSummaryProducts = (orders: PurchaseOrder[]): OrderWaitingSummaryProduct[] => {
  const productsByKey = new Map<string, OrderWaitingSummaryProduct>();

  for (const order of orders) {
    for (const lineItem of order.lineItems ?? []) {
      const productTitle = lineItem.productTitle ?? "";
      const bagSizeGrams = getUnitWeightGrams(lineItem);
      const grindType = lineItem.grindType;
      const key = `${productTitle}|${bagSizeGrams ?? ""}|${grindType ?? ""}`;

      const parsedQuantity = Number.parseInt(String(lineItem.quantity), 10);
      const quantity = Number.isNaN(parsedQuantity) ? 0 : parsedQuantity;
      const weightInGrams = Number(lineItem.lineItemWeightInGrams ?? 0);

      const existing = productsByKey.get(key);
      productsByKey.set(key, {
        key,
        productTitle,
        bagSizeGrams,
        grindType,
        totalUnits: (existing?.totalUnits ?? 0) + quantity,
        totalWeight: (existing?.totalWeight ?? 0) + weightInGrams,
      });
    }
  }

  return Array.from(productsByKey.values()).sort(
    (a, b) => a.productTitle.localeCompare(b.productTitle) || (a.bagSizeGrams ?? 0) - (b.bagSizeGrams ?? 0)
  );
};

// exportToCsv requires a Record<string, unknown> row shape — interfaces don't carry an implicit
// index signature, so cast at the boundary rather than loosening OrderWaitingSummaryProduct itself.
type OrderWaitingSummaryCsvRow = Record<string, unknown>;

export const exportOrderWaitingSummaryToCsv = (products: OrderWaitingSummaryProduct[], t: TFunction): void => {
  const columns: CsvColumn<OrderWaitingSummaryCsvRow>[] = [
    { key: "productTitle", header: t("views.orderWaitingSummaryDialog.exportColumns.product") },
    { key: "totalUnits", header: t("views.orderWaitingSummaryDialog.exportColumns.units") },
    {
      key: "totalWeight",
      header: t("views.orderWaitingSummaryDialog.exportColumns.weight"),
      formatter: (value) => t("weight.weightWithSuffix", { grams: value }) as string,
    },
  ];
  exportToCsv({ data: products as unknown as OrderWaitingSummaryCsvRow[], filename: "waiting-to-be-fulfilled-summary", columns });
};
